import { City, Country } from "country-state-city";

function normalizeLookupValue(value) {
  return String(value || "").trim().toLowerCase();
}

export function getFlagEmoji(countryCode) {
  const normalizedCode = String(countryCode || "").trim().toUpperCase();

  if (!/^[A-Z]{2}$/.test(normalizedCode)) {
    return "";
  }

  return Array.from(normalizedCode)
    .map((character) => String.fromCodePoint(127397 + character.charCodeAt(0)))
    .join("");
}

const COUNTRY_OPTIONS = Country.getAllCountries()
  .map((country) => ({
    value: String(country.name || "").trim(),
    label: String(country.name || "").trim(),
    isoCode: String(country.isoCode || "").trim().toUpperCase(),
    flagCode: String(country.isoCode || "").trim().toLowerCase(),
    flagEmoji: getFlagEmoji(country.isoCode)
  }))
  .filter((country) => country.value && country.isoCode)
  .sort((leftValue, rightValue) => leftValue.label.localeCompare(rightValue.label));

const COUNTRY_OPTIONS_BY_NAME = new Map(COUNTRY_OPTIONS.map((country) => [normalizeLookupValue(country.label), country]));
const CITY_OPTIONS_BY_COUNTRY = new Map();

function filterOptions(options, query, limit) {
  const normalizedQuery = normalizeLookupValue(query);

  if (!normalizedQuery) {
    return options.slice(0, limit);
  }

  return options
    .filter((option) => normalizeLookupValue(option.label).includes(normalizedQuery))
    .slice(0, limit);
}

function buildCityOptions(countryName) {
  const country = getCountryOptionByName(countryName);

  if (!country) {
    return [];
  }

  const cacheKey = country.isoCode;

  if (CITY_OPTIONS_BY_COUNTRY.has(cacheKey)) {
    return CITY_OPTIONS_BY_COUNTRY.get(cacheKey);
  }

  const seenCityNames = new Set();
  const cityOptions = City.getCitiesOfCountry(country.isoCode)
    .map((city) => String(city?.name || "").trim())
    .filter((cityName) => {
      if (!cityName) {
        return false;
      }

      const normalizedCityName = normalizeLookupValue(cityName);

      if (seenCityNames.has(normalizedCityName)) {
        return false;
      }

      seenCityNames.add(normalizedCityName);
      return true;
    })
    .map((cityName) => ({ value: cityName, label: cityName }))
    .sort((leftValue, rightValue) => leftValue.label.localeCompare(rightValue.label));

  CITY_OPTIONS_BY_COUNTRY.set(cacheKey, cityOptions);
  return cityOptions;
}

export function getCountryOptionByName(countryName) {
  return COUNTRY_OPTIONS_BY_NAME.get(normalizeLookupValue(countryName)) || null;
}

export function getCountrySuggestions(query, limit = 20) {
  return filterOptions(COUNTRY_OPTIONS, query, limit);
}

export function getAllCountryOptions() {
  return COUNTRY_OPTIONS;
}

export function getCityOptionByName(countryName, cityName) {
  const cityOptions = buildCityOptions(countryName);
  return cityOptions.find((city) => normalizeLookupValue(city.label) === normalizeLookupValue(cityName)) || null;
}

export function getAllCityOptions(countryName) {
  return buildCityOptions(countryName);
}

export function getCitySuggestions(countryName, query, limit = 30) {
  return filterOptions(buildCityOptions(countryName), query, limit);
}